import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { SUPPORTED_LOCALES } from '@/lib/i18n'

import { useAdminMutation, useAdminQuery } from '../api'
import type { Juror } from '../types'
import { SelectField, TextField } from './fields'

/**
 * Le jury d'un concours : trois ou quatre personnes, chacune avec son lien.
 *
 * Un juré n'a pas de compte : il note depuis son téléphone, avec un lien
 * personnel qu'on lui montre en QR ou qu'on lui envoie par e-mail, dans sa
 * langue. Ce lien est un secret — il donne le pouvoir de noter :
 *
 * * **régénérer** le remplace (téléphone perdu, lien transféré) ; l'ancien ne
 *   vaut plus rien, les notes déjà données restent ;
 * * **révoquer** le coupe tout à fait ;
 * * un juré qui a déjà noté ne se **supprime** pas — ses notes font des scores ;
 * * **modifier** corrige son nom, son adresse ou sa langue : ses notes tiennent
 *   à son identifiant, son lien aussi — rien à lui renvoyer.
 */
/** Les bornes du serveur pour un nom de juré, espaces de bord retirés. */
const NAME_MIN = 2
const NAME_MAX = 120

/** Le nom tel que le serveur l'enregistrera : espaces multiples réduits. */
function cleanName(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

export function JuryPanel({
  eventId,
  competitionCategoryId,
  title,
}: {
  eventId: string | undefined
  competitionCategoryId: string
  title: string
}) {
  const { t } = useTranslation()
  const localeOptions = [
    { value: '', label: t('admin.jury.localeDefault') },
    ...SUPPORTED_LOCALES.map((code) => ({ value: code, label: code.toUpperCase() })),
  ]
  const path = `/admin/events/${eventId}/competition/categories/${competitionCategoryId}/jurors`
  const key = ['admin-jurors', competitionCategoryId]
  const query = useAdminQuery<Juror[]>(key, path, eventId)
  const refresh = [key, ['admin-standings', competitionCategoryId]]

  const create = useAdminMutation<Juror, { name: string; email: string | null; locale: string | null }>(
    'post',
    () => path,
    eventId,
    refresh,
  )
  const act = useAdminMutation<unknown, { id: string; action: 'regenerate' | 'revoke' | 'invite' }>(
    'post',
    ({ id, action }) => `${path}/${id}/${action}`,
    eventId,
    refresh,
  )
  const update = useAdminMutation<
    Juror,
    { id: string; name: string; email: string | null; locale: string | null }
  >('patch', ({ id }) => `${path}/${id}`, eventId, refresh)
  const remove = useAdminMutation<unknown, { id: string }>(
    'delete',
    ({ id }) => `${path}/${id}`,
    eventId,
    refresh,
  )

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [locale, setLocale] = useState('')
  // L'action qui attend sa confirmation : régénérer et révoquer coupent un
  // lien peut-être déjà dans la poche du juré.
  const [pending, setPending] = useState<{ id: string; action: 'regenerate' | 'revoke' } | null>(
    null,
  )
  const [shown, setShown] = useState<string | null>(null)
  // Le juré en cours de modification, avec sa saisie.
  const [editing, setEditing] = useState<{
    id: string
    name: string
    email: string
    locale: string
  } | null>(null)

  const jurors = query.data ?? []
  const error = create.error ?? act.error ?? remove.error ?? update.error
  const editedName = editing ? cleanName(editing.name) : ''

  return (
    <Card className="space-y-5">
      <div>
        <h3 className="font-display text-heading-md text-content-primary">{title}</h3>
        <p className="mt-1 text-caption text-content-muted">{t('admin.jury.hint')}</p>
      </div>

      {query.isPending ? <Skeleton className="h-16 w-full" /> : null}
      {jurors.length === 0 && !query.isPending ? (
        <p className="text-sm text-content-secondary">{t('admin.jury.empty')}</p>
      ) : null}

      <ul className="space-y-3">
        {jurors.map((juror) => (
          <li key={juror.id} className="rounded-lg border border-line-subtle px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-content-primary">{juror.name}</p>
                <p className="truncate text-caption text-content-muted">
                  {[juror.email, juror.locale?.toUpperCase(), t('admin.jury.marks', { count: juror.marks })]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <span className="flex flex-wrap gap-2">
                {juror.revoked ? <Badge tone="ember">{t('admin.jury.revoked')}</Badge> : null}
                {juror.invited_at ? <Badge tone="success">{t('admin.jury.invited')}</Badge> : null}
              </span>
            </div>

            {shown === juror.id && juror.url && juror.svg ? (
              <div className="mt-3 flex flex-col items-center gap-2">
                <div className="w-48 rounded-md bg-white p-2">
                  <img src={juror.svg} alt="" className="h-full w-full" />
                </div>
                <p className="break-all text-center text-caption text-content-muted">{juror.url}</p>
              </div>
            ) : null}

            {editing?.id === juror.id ? (
              <div className="mt-3 space-y-3">
                <TextField
                  label={t('admin.jury.name')}
                  hint={t('admin.jury.nameHint')}
                  value={editing.name}
                  maxLength={NAME_MAX}
                  onChange={(value) => setEditing({ ...editing, name: value })}
                />
                <TextField
                  label={t('admin.jury.email')}
                  hint={t('admin.jury.emailHint')}
                  type="email"
                  value={editing.email}
                  onChange={(value) => setEditing({ ...editing, email: value })}
                />
                <SelectField
                  label={t('admin.jury.locale')}
                  value={editing.locale}
                  onChange={(value) => setEditing({ ...editing, locale: value })}
                  options={localeOptions}
                />
                <p className="text-caption text-content-muted">{t('admin.jury.editHint')}</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={
                      update.isPending ||
                      editedName.length < NAME_MIN ||
                      editedName.length > NAME_MAX
                    }
                    onClick={() =>
                      update.mutate(
                        {
                          id: juror.id,
                          name: editedName,
                          email: editing.email.trim() || null,
                          locale: editing.locale || null,
                        },
                        { onSuccess: () => setEditing(null) },
                      )
                    }
                  >
                    {t('admin.save')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                    {t('admin.cancel')}
                  </Button>
                </div>
              </div>
            ) : pending?.id === juror.id ? (
              <div className="mt-3 space-y-2">
                <p className="text-caption text-state-warning">
                  {t(`admin.jury.confirm.${pending.action}`)}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="ember"
                    disabled={act.isPending}
                    onClick={() => act.mutate(pending, { onSettled: () => setPending(null) })}
                  >
                    {t(`admin.jury.action.${pending.action}`)}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setPending(null)}>
                    {t('admin.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    update.reset()
                    setPending(null)
                    setEditing({
                      id: juror.id,
                      name: juror.name,
                      email: juror.email ?? '',
                      locale: juror.locale ?? '',
                    })
                  }}
                >
                  {t('admin.jury.action.edit')}
                </Button>
                {juror.url ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShown(shown === juror.id ? null : juror.id)}
                  >
                    {shown === juror.id ? t('admin.jury.hideLink') : t('admin.jury.showLink')}
                  </Button>
                ) : null}
                {juror.url && juror.email ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: juror.id, action: 'invite' })}
                  >
                    {t('admin.jury.action.invite')}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPending({ id: juror.id, action: 'regenerate' })}
                >
                  {t('admin.jury.action.regenerate')}
                </Button>
                {!juror.revoked ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPending({ id: juror.id, action: 'revoke' })}
                  >
                    {t('admin.jury.action.revoke')}
                  </Button>
                ) : null}
                {juror.marks === 0 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate({ id: juror.id })}
                  >
                    {t('admin.jury.action.delete')}
                  </Button>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="grid gap-3 border-t border-line-subtle pt-4 sm:grid-cols-3">
        <TextField label={t('admin.jury.name')} value={name} onChange={setName} />
        <TextField
          label={t('admin.jury.email')}
          hint={t('admin.jury.emailHint')}
          type="email"
          value={email}
          onChange={setEmail}
        />
        <SelectField
          label={t('admin.jury.locale')}
          value={locale}
          onChange={setLocale}
          options={localeOptions}
        />
      </div>
      <Button
        size="sm"
        disabled={cleanName(name).length < NAME_MIN || create.isPending}
        onClick={() =>
          create.mutate(
            { name: cleanName(name), email: email.trim() || null, locale: locale || null },
            {
              onSuccess: () => {
                setName('')
                setEmail('')
                setLocale('')
              },
            },
          )
        }
      >
        {t('admin.jury.add')}
      </Button>

      {error ? (
        <p className="text-caption text-state-danger">
          {error instanceof ApiError && error.code === 'JUROR_NAME_TAKEN'
            ? t('admin.jury.nameTaken')
            : error.message}
        </p>
      ) : null}
    </Card>
  )
}
